import { Box, TablePagination } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import type { FC, ReactNode } from 'react';

import { useSelectedItems } from '@core/hooks/useSelectedItems';
import type { ReminderUserSettingSummary } from '@core/types/api/ReminderUserSettingSummary/ReminderUserSettingSummary';
import { hasValue } from '@core/utils/equality';
import { type NotificationCardAction, reminderCardItem } from '@core/utils/notificationCard';
import {
  type NotificationFilterValues,
  type NotificationSortOption,
  reminderFilters,
  reminderSort,
} from '@core/utils/notificationQueryParams';
import { useEmptyPageGuard, usePagination } from '@web/hooks/usePagination';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';
import { useNotificationPanel } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { SkeletonList } from '../SkeletonList/SkeletonList';
import { NotificationCard, NotificationCardSkeleton } from './NotificationCard';
import {
  NotificationCenterEmptyState,
  reminderEmptyStateText,
} from './NotificationCenterEmptyState';
import { NotificationsBulkActions } from './NotificationsBulkActions';

type Props = {
  search: string;
  filterValues: NotificationFilterValues;
  sortOption: NotificationSortOption;
  onEditReminder: (reminderID: string) => void;
};

export const UserRemindersList: FC<Props> = ({
  search,
  filterValues,
  sortOption,
  onEditReminder,
}) => {
  const { scope, scopeObject } = useNotificationPanel();
  const scopeObjectId = hasValue(scopeObject) ? scopeObject.id : null;

  const { page, setPage, perPage } = usePagination({
    resetOn: [search, scope, scopeObjectId, filterValues, sortOption],
    initialPerPage: 25,
  });

  const { data, isPending, isFetching } = useSlabQuery(
    'GET reminder user setting summaries',
    {
      queryParams: {
        perPage,
        page,
        filterBy: reminderFilters({ search, scope, scopeObject, filterValues }),
        sortBy: reminderSort(sortOption),
      },
    },
    { placeholderData: keepPreviousData },
  );

  const confirm = useConfirmDialog();
  const toast = useToast({ subjectName: 'Reminder' });
  const { mutate: archiveReminders } = useSlabMutation('POST bulk update reminder user settings', {
    onSuccess: (_, { args }) => {
      const count = args.body.length;
      toast.success({ message: count > 1 ? `${count} Reminders deleted` : 'Reminder deleted' });
    },
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error, 'Failed to archive') });
    },
  });

  const confirmArchive = (ids: string[], onArchived?: () => void): void => {
    const subjectName = ids.length > 1 ? `these ${ids.length} reminders` : 'this reminder';
    confirm.alert({
      title: 'Confirm Delete',
      description: `Are you sure you want to delete ${subjectName}?`,
      actions: [
        { label: 'Cancel', variant: 'slab-primary' },
        {
          label: 'Delete',
          variant: 'slab-secondary',
          onClick: (): void => {
            archiveReminders(
              { args: { body: ids.map((id) => ({ id, isArchived: true })) } },
              { onSuccess: onArchived },
            );
          },
        },
      ],
    });
  };

  const handlePressAction = (
    action: NotificationCardAction,
    reminder: ReminderUserSettingSummary,
  ): void => {
    if (action === 'delete') {
      confirmArchive([reminder.id]);
      return;
    }

    if (action === 'edit') {
      onEditReminder(reminder.id);
    }
  };

  const reminders = data?.items ?? [];
  const totalCount = data?.count ?? 0;

  useEmptyPageGuard({ page, setPage, itemCount: reminders.length, isFetching });

  const { selectedKeys, isSelected, isAllSelected, setSelected, setAllSelected, clearSelection } =
    useSelectedItems({
      keys: reminders.map((reminder) => reminder.id),
      resetOn: [search, scope, scopeObjectId, filterValues, page],
    });

  const handleBulkDelete = (): void => confirmArchive(selectedKeys, clearSelection);

  const content = ((): ReactNode => {
    if (isPending) {
      return (
        <SkeletonList>
          <NotificationCardSkeleton />
        </SkeletonList>
      );
    }

    if (reminders.length === 0) {
      return (
        <NotificationCenterEmptyState {...reminderEmptyStateText({ search, scope, scopeObject })} />
      );
    }

    return reminders.map((reminder) => (
      <NotificationCard
        key={reminder.id}
        item={reminderCardItem(reminder)}
        onAction={(action) => handlePressAction(action, reminder)}
        isSelected={isSelected(reminder.id)}
        onSelectedChange={(isChecked): void => setSelected(reminder.id, isChecked)}
      />
    ));
  })();

  return (
    <Box display='flex' flexDirection='column' flex={1} minHeight={0}>
      {selectedKeys.length > 0 && (
        <Box paddingTop={2}>
          <NotificationsBulkActions
            selectedCount={selectedKeys.length}
            isAllSelected={isAllSelected}
            onSelectAllChange={setAllSelected}
            onDelete={selectedKeys.length > 1 ? handleBulkDelete : undefined}
            onClear={clearSelection}
          />
        </Box>
      )}

      <Box
        flex={1}
        minHeight={0}
        overflow='auto'
        paddingTop={selectedKeys.length > 0 ? 0 : 2}
        paddingBottom={2}
        display='flex'
        flexDirection='column'
      >
        {content}
      </Box>

      {totalCount > perPage && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          gap={1}
          paddingX={1}
          borderTop='1px solid'
          borderColor='SlabGray.Stroke'
        >
          {isFetching && !isPending && <LoadingSpinner size={16} wrapInBox={false} />}
          <TablePagination
            component='div'
            rowsPerPageOptions={[]}
            count={totalCount}
            rowsPerPage={perPage}
            page={page}
            onPageChange={(_, nextPage): void => setPage(nextPage)}
            labelDisplayedRows={({ from, to, count }): string => `${from}–${to} of ${count}`}
            sx={{
              '> .MuiToolbar-root': { minHeight: 0, paddingLeft: 0 },
              '& p': { fontSize: 'inherit', lineHeight: 1, margin: 0 },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
